import React from 'react';
import clsx from 'clsx';
import { Badge, Button } from '@dopomogai/ui';

export type ProblemSolutionItem = {
  problem: string;
  solutions: string[];
  accent?: 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose' | 'slate';
};

export type ProblemSolutionSectionProps = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  items: ProblemSolutionItem[];
  onAction?: (actionId: string) => void;
  cta?: { label: string; actionId: string };
  className?: string;
};

const stripe = (accent?: string) =>
  clsx('absolute inset-x-2 top-2 h-2 rounded-full', {
    'bg-emerald-400': accent === 'emerald' || !accent,
    'bg-violet-400': accent === 'violet',
    'bg-blue-400': accent === 'blue',
    'bg-cyan-400': accent === 'cyan',
    'bg-amber-400': accent === 'amber',
    'bg-rose-400': accent === 'rose',
    'bg-slate-400': accent === 'slate',
  });

export const ProblemSolutionSection: React.FC<ProblemSolutionSectionProps> = ({
  eyebrow,
  title,
  subtitle,
  items,
  onAction,
  cta,
  className,
}) => {
  if (!items || !items.length) return null;

  return (
    <section className={clsx('relative bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {(eyebrow || title || subtitle) && (
          <div className="mb-6 md:mb-8">
            {eyebrow && (
              <div className="mb-2">
                <Badge text={eyebrow} accentColor="green" />
              </div>
            )}
            {title && <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">{title}</h2>}
            {subtitle && <p className="mt-3 text-slate-700 text-base md:text-lg max-w-3xl">{subtitle}</p>}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {items.map((it, i) => (
            <div key={i} className="relative border-2 border-black rounded-2xl bg-white p-5 md:p-6">
              <div aria-hidden="true" className={stripe(it.accent)} />
              <div className="font-extrabold text-slate-900 text-lg md:text-xl">{it.problem}</div>
              <ul className="mt-3 space-y-2">
                {it.solutions.map((s, j) => (
                  <li key={j} className="flex items-start gap-2 text-slate-800">
                    <span className="inline-block mt-[6px] w-2 h-2 rounded-full bg-emerald-500" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {cta ? (
          <div className="mt-8">
            <Button variant="secondary" text={cta.label} onClick={() => onAction?.(cta.actionId)} />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
